// © Microsoft Corporation. All rights reserved.
import { darkModeStylesheetBehavior, display, focusVisible, forcedColorsStylesheetBehavior } from "@microsoft/fast-foundation";
import { accentFillActiveBehavior, accentFillHoverBehavior, elevation, neutralDividerRestBehavior, neutralFillHoverBehavior, neutralFillStealthHoverBehavior, neutralFocusBehavior, neutralForegroundActiveBehavior, neutralForegroundRestBehavior, neutralLayerFloatingBehavior } from "@fluentui/web-components";
import { css } from "@microsoft/fast-element";
import { SystemColors } from "@microsoft/fast-web-utilities";

export const navBarStyles = css`
    ${display("flex")} :host {
        align-items: center;
        color: ${neutralForegroundRestBehavior.var};
        fill: ${neutralForegroundRestBehavior.var};
    }

    .list {
        height: auto;
        overflow: hidden;
        list-style: none;
        padding-inline: 0 15px;
        margin: 0;
        display: inline-flex;
        align-items: center;
    }

    li {
        list-style: none;
        padding: 0 16px;
        display: flex;
    }

    li:first-child {
        padding-inline-start: 0;
    }

    li:last-child,
    li.pipe {
        padding-inline-end: 0;
    }

    .hidden {
        visibility: hidden;
    }

    .overflowContainer {
        position: relative;
    }

    .overflowContainer fluent-button svg {
        vertical-align: middle;
    }

    .overflowContainer .hidden {
        display: none;
    }

    .overflowContainer ul li {
        padding: 5px 0;
        margin: 0 calc((10 + (var(--design-unit) * 2 * var(--density))) * 1px);
    }

    .endContainer {
        display: flex;
        margin-inline-start: auto;
        align-items: center;
    }

    .overflowMenu {
        position: absolute;
        --elevation: 11;
        background: ${neutralLayerFloatingBehavior.var};
        border: calc(var(--outline-width) * 1px) solid transparent;
        border-radius: var(--elevated-corner-radius);
        ${elevation}
        margin: 0;
        padding: 0;
        max-width: 368px;
        min-width: 100px;
        z-index: 3;
        inset-inline-end: 0;
    }

    .pipe::after {
        content: "";
        height: 14px;
        align-self: center;
        padding-inline-start: 18px;
        border-inline-end: 1px solid #cecece;
    }

    fluent-anchor {
        font-family: var(--body-font);
        outline: none;
        border-radius: calc(var(--corner-radius) * 1px);
        border: calc(var(--focus-outline-width) * 1px) solid transparent;
        font-family: var(--body-font);
        outline: none;
        border-radius: calc(var(--corner-radius) * 1px);
        border: calc(var(--focus-outline-width) * 1px) solid transparent;
    }

    .control {
        font-size: var(--type-ramp-base-font-size);
        line-height: var(--type-ramp-base-line-height);
        box-sizing: border-box;
        justify-content: center;
        align-items: center;
        white-space: nowrap;
        outline: none;
        text-decoration: none;
        cursor: pointer;
        color: ${neutralForegroundRestBehavior.var};
        fill: ${neutralForegroundRestBehavior.var};
        border-bottom: 2px solid transparent;
    }

    .active .control {
        border-bottom-color: ${accentFillActiveBehavior.var};
        color: ${neutralForegroundActiveBehavior.var};
        fill: ${neutralForegroundActiveBehavior.var};
        font-weight: 600;
    }

    .active .control:hover {
        border-bottom-color: ${accentFillHoverBehavior.var};
    }

    .active .control:active {
        border-bottom-color: ${accentFillActiveBehavior.var};
    }

    .control:hover {
        border-bottom-color: ${neutralFillHoverBehavior.var};
    }

    .control:active {
        border-bottom-color: ${neutralForegroundActiveBehavior.var};
    }

    fluent-anchor([active]:${focusVisible}),
    fluent-anchor(:${focusVisible}) {
        border-color: ${neutralFocusBehavior.var};
    }

    .control::-moz-focus-inner {
        border: 0;
    }

    fluent-anchor(.disabled) {
        opacity: var(--disabled-opacity);
    }

    fluent-anchor::slotted([slot="start"]) {
        vertical-align: middle;
    }`
    .withBehaviors(
        neutralLayerFloatingBehavior,
        neutralForegroundRestBehavior,
        forcedColorsStylesheetBehavior(
            css`
                .control {
                    border-bottom-color: ${SystemColors.ButtonFace};
                    color: ${SystemColors.LinkText};
                    fill: currentColor;
                }

                .active .control {
                    border-bottom-color: ${SystemColors.LinkText};
                    color: ${SystemColors.LinkText};
                    fill: currentColor;
                }
            `
        )
    );

export const brandingStyles = css`
    ${display("flex")} :host {
        align-items: normal;
    }

    li {
        padding: 0px 12px;
    }

    .overflowContainer fluent-button svg {
        color: var(--brand-nav-item-color);;
        fill: var(--brand-nav-item-color);;
    }

    fluent-button::part(control):${focusVisible} {
        border-width: 1px;
        box-shadow: none;
    }

    msn-nav-item.brand-nav::part(control) {
        font-size: 20px;
        font-weight: 700;
        line-height: normal;
    }

    msn-nav-item.brand-nav {
        margin-inline: 0px 40px;
        align-items: center;
    }

    msn-nav-item#nav-shopping,
    msn-nav-item#nav-travel,
    msn-nav-item#nav-recipes {
        align-items: end;
    }

    msn-nav-item#nav-personalize,
    msn-nav-item#nav-qna {
        align-items: normal;
    }

    .brand-pipe {
        display: inline-flex;
        align-items: center;
    }

    .brand-pipe::after {
        content: "";
        height: 16px;
        border-inline-start: 1px solid #cecece;
        margin-inline: 0 12px;
    }

    .home-icon {
        margin-inline-end: 6px;
    }

    .home-icon svg,
    .nav-icon svg {
        fill: var(--brand-nav-item-color);
        height: 20px;
        width: 20px;
    }
    
    .home-icon svg:hover {
        background: ${neutralFillStealthHoverBehavior.var};
    }

    msn-nav-item#nav-news::part(control) {
        font-family: "EB Garamond", Segoe UI, Segoe UI Midlevel, Arial, Sans-Serif;
        font-size: 21px;
        font-weight: 800;
        line-height: 24px;
    }

    msn-nav-item#nav-sports::part(control) {
        font-family: Bahnschrift, Segoe UI, Segoe UI Midlevel, Arial, Sans-Serif;
    }

    msn-nav-item#nav-weather::part(control),
    msn-nav-item#nav-traffic::part(control),
    msn-nav-item#nav-finance::part(control) {
        font-family: Bierstadt, Segoe UI, Segoe UI Midlevel, Arial, Sans-Serif;
        font-size: 21px;
    }

    msn-nav-item#nav-travel::part(control),
    msn-nav-item#nav-recipes::part(control),
    msn-nav-item#nav-shopping::part(control) {
        font-family: Tenorite, Segoe UI, Segoe UI Midlevel, Arial, Sans-Serif;
        font-size: 23px;
        vertical-align: middle;
    }

    msn-nav-item#nav-qna::part(control) {
        font-family: Lora, Segoe UI, Segoe UI Midlevel, Arial, Sans-Serif;
    }`
    .withBehaviors(
        neutralDividerRestBehavior,
        neutralFillStealthHoverBehavior
    );
